import { fg } from '@atlaskit/platform-feature-flags';
import {
	createClient,
	type DCSpaceFilterOption,
	jiraDcProjectFilterOptionsQuery,
	type JiraProject,
	jiraProjectsQuery,
	JiraScope,
	spaceARIEntitiesQuery,
} from '@atlassian/search-client';

import { ThirdPartyConfigs } from '../../../../../common/constants/3p-product-configs';
import { DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI } from '../../../../../common/constants/entities';
import { type CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import {
	ProductKeys,
	type ProductKeys1P,
	type ProductKeys3P,
} from '../../../../../common/constants/products';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const projectIdToARI = (cloudId: string, projectId: string) =>
	`ari:cloud:jira:${cloudId}:project/${projectId}`;

export const searchedProjectLabel = ({
	projectKey,
	name,
}: {
	projectKey: string;
	name: string;
}) => {
	const key = `(${projectKey})`;
	if (name.toLowerCase().includes(key.toLowerCase()) || !fg('fix_project_filter_lookup')) {
		return name;
	}

	return `${name} ${key}`;
};

const searchProject = async (query: string, config: CloudConfig) => {
	const searchClient = createClient({
		cloudId: config.cloudId || '',
	});

	let searchProjectsResponse, searchedProjects: JiraProject[];

	const product = config.product;
	if (product === ProductKeys.JiraDC || product === ProductKeys.JsmDC) {
		searchProjectsResponse = await jiraDcProjectFilterOptionsQuery({
			variables: {
				query,
				cloudIdARI: `ari:cloud:confluence::site/${config.cloudId}`,
				limit: 5,
				entities: DEFAULT_CLOUD_GRAPH_SPACE_ENTITY_ATI,
				product: ProductKeys.JiraDC,
				integration: fg('search_sidebar_fanout_enabled')
					? config.selectedDatasource?.integrationAri
					: ThirdPartyConfigs.get(product)?.integrationAri,
				containerTypes: fg('is_jsm_dc_enabled_in_ui')
					? ThirdPartyConfigs.get(product)?.containerTypes
					: [],
			},
		});
		return (
			searchProjectsResponse?.data?.search?.search?.edges?.map((edge: DCSpaceFilterOption) => ({
				trackingKey: edge.node.entity.id,
				value: edge.node.entity.id,
				label: searchedProjectLabel({
					projectKey: edge.node.entity.key,
					name: edge.node.entity.displayName,
				}),
				avatarUrl: edge?.node?.entity?.icon?.url,
				queryParamValue: edge.node.entity.id,
				product: config.product,
			})) || []
		);
	}

	searchProjectsResponse = await searchClient.search({
		query,
		resultLimit: 5,
		scopes: [JiraScope.JiraProject],
		analytics: {
			identifier: 'search-page',
		},
	});
	searchedProjects =
		searchProjectsResponse.response.rawData.scopes.length > 0
			? searchProjectsResponse.response.rawData.scopes[0].results.map((projectResult) => ({
					projectId: projectResult.id,
					name: projectResult.name,
					projectKey: projectResult.attributes.projectKey,
					avatar: {
						small:
							'url' in projectResult.attributes.avatar
								? projectResult.attributes.avatar.url
								: undefined,
					},
				}))
			: [];

	return searchedProjects.map((project) => ({
		trackingKey: projectIdToARI(config.cloudId || '', project.projectId),
		value: projectIdToARI(config.cloudId || '', project.projectId),
		label: searchedProjectLabel(project),
		avatarUrl: project.avatar?.small,
		queryParamValue: project.projectId,
		product: config.product,
	}));
};

const productKeys1P: ProductKeys1P[] = [
	ProductKeys.Jira,
	ProductKeys.JiraProductDiscovery,
	ProductKeys.JiraServiceManagement,
];

const products3PFromConfig: ProductKeys3P[] = getProducts3PFromConfigForFilter('project');

export const filter = createMultiSelectFilter({
	id: 'project',
	products: [...productKeys1P, ...products3PFromConfig],
	alwaysVisible: true,
	universal: false,
	queryParams: {
		key: 'jProject',
		fetcher: async (queryParams, config) => {
			if (config.product === ProductKeys.JiraDC || config.product === ProductKeys.JsmDC) {
				const initialProjectsResponse = await spaceARIEntitiesQuery({
					variables: {
						ids: queryParams,
					},
				});

				const projectOptions = initialProjectsResponse.data?.external_entities?.space?.map(
					(space) => ({
						trackingKey: space.id,
						value: space.id,
						label: space.displayName,
						avatarUrl: space.icon?.url || '',
						queryParamValue: space.id,
						product: config.product,
					}),
				);

				return projectOptions || [];
			} else {
				const initialProjectARIs = queryParams.map((projectId) =>
					projectIdToARI(config.cloudId || '', projectId),
				);
				const initialProjectsResponse = await jiraProjectsQuery(initialProjectARIs);
				const initialProjects: JiraProject[] =
					initialProjectsResponse.data?.jira.jiraProjects ?? [];

				const allProjects: JiraProject[] = initialProjects;
				const projectOptions = allProjects.map((project) => ({
					trackingKey: projectIdToARI(config.cloudId || '', project.projectId),
					value: projectIdToARI(config.cloudId || '', project.projectId),
					label: project.name,
					avatarUrl: project.avatar?.small,
					queryParamValue: project.projectId,
					product: config.product,
				}));
				return projectOptions;
			}
		},
	},
	initialOptions: {
		fetcher: async (config) => {
			return searchProject('', config);
		},
	},
	lookup: {
		fetcher: async (query, config) => {
			return searchProject(query, config);
		},
	},
	isSuggestedFilter: (primaryProduct) =>
		primaryProduct === ProductKeys.Jira ||
		primaryProduct === ProductKeys.JiraDC ||
		primaryProduct === ProductKeys.JsmDC ||
		primaryProduct === ProductKeys.JiraServiceManagement ||
		primaryProduct === ProductKeys.JiraProductDiscovery,
});
